import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import BaseDetails from '../../../Base/BaseDetails';
import { toggleModal, openDetails, closeDetails } from 'reducers/ui';
import isEmpty from 'lodash/isEmpty';
import staticText from 'static/text_display.json';
import { translatedKeyValueText } from 'utils/methods/helpers';
import { ROLES } from 'utils/constants';
import {
  eventsNS,
  eventsGridActions,
  eventSelectors,
  eventsPageActions,
} from 'containers/Pages/Events';
import FollowupButtonComponent from './followupButtonComponent';
import FollowupEventDetailsPanel from './followupDetailPanel';
import { PAGE_URLS } from 'utils/constants/';

const EventDetails = ({
  toggleEditMode,
  id,
  cancel,
  editable,
  OperationalComponent,
  loading,
  downloadable,
}) => {
  //----------------Selectors------------------------------------
  const eventDetails = useSelector(state =>
    eventSelectors.getEventDetailsTransformedSectioned(state),
  );
  const isSupervisor = useSelector(state => state.user.general.roles);
  const followupEventID = useSelector(state =>
    eventSelectors.getFollowupEventID(state),
  );
  // for operational component
  const eventForOperationalComponent = useSelector(state =>
    eventSelectors.getEventDetailsForOperationalComponent(state),
  );
  const qualifiedToSubmitReview = useSelector(state =>
    eventSelectors.checkIfQualifiedToSubmitReview(state),
  );
  const isQualifiedToPublish = useSelector(state =>
    eventSelectors.checkIfQualifiedToPublish(state),
  );
  const qualifiedToReview = useSelector(state =>
    eventSelectors.checkIfqualifiedToReview(state),
  );
  const qualifiedToDistributeAndAssign = useSelector(state =>
    eventSelectors.checkQualifiedToDistributeAndAssign(state),
  );
  //---------------------------
  const rawEventsData = useSelector(state =>
    eventSelectors.getEventDetailsRaw(state),
  );
  const operation = props => (
    <OperationalComponent
      {...props}
      event={eventForOperationalComponent}
      rawEventsData={rawEventsData}
      qualifiedToSubmitReview={qualifiedToSubmitReview}
      isQualifiedToPublish={isQualifiedToPublish}
      qualifiedToReview={qualifiedToReview}
      qualifiedToDistributeAndAssign={qualifiedToDistributeAndAssign}
    />
  );
  //----------------------Action------------------------------------
  const dispatch = useDispatch();
  const dispatchFetchEventDetailsByID = useCallback(
    id => {
      dispatch(eventsGridActions.fetchGridRowDetails({ id }));
    },
    [dispatch],
  );
  const dispatchToggleModal = useCallback(() => {
    dispatch(toggleModal(eventsNS));
  }, [dispatch]);
  const dispatchClearEvents = useCallback(() => {
    dispatch(eventsGridActions.setGridRowDetails({ details: {} }));
  }, [dispatch]);

  const dispatchClearEventID = useCallback(() => {
    dispatch(eventsGridActions.setSelectedRowID(null));
  }, [dispatch]);
  const dispatchFetchEventDetailsPdf = useCallback(
    id => {
      dispatch(eventsPageActions.printEvent({ id }));
    },
    [dispatch],
  );
  const onDownloadClick = () => {
    dispatchFetchEventDetailsPdf(id);
  };
  useEffect(() => {
    if (id) {
      dispatchFetchEventDetailsByID(id);
      dispatch(openDetails(PAGE_URLS.EVENTS.link));
      dispatch(eventsGridActions.setSelectedRowID(id));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onEdit = () => {
    toggleEditMode();
    dispatchToggleModal();
  };

  const followupComponent = useCallback(
    props => (
      <FollowupButtonComponent
        {...props}
        toggleEditMode={toggleEditMode}
        toggleModal={dispatchToggleModal}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  //the published version of the event should not be editable
  const checkEditable = () => {
    const isDistributionInternal =
      eventDetails?.detailedInfo?.publishing.value.distribution;
    // the person is a supervisor and the distribution is not internal, do not give permission to edit
    if (
      isSupervisor.includes(ROLES.SUPERVISOR.id) &&
      isDistributionInternal !== 'INTERNAL'
    ) {
      return false;
    } else if (
      eventDetails.others.id.value !==
      eventDetails.detailedInfo.publishing.value.publishedId
    ) {
      return editable;
    } else {
      return false;
    }
  };

  const onCancel = () => {
    cancel();
    dispatch(closeDetails(PAGE_URLS.EVENTS.link));
    dispatchClearEvents();
    dispatchClearEventID();
  };
  return (
    <>
      <BaseDetails
        header={staticText.operational_view.event.details}
        editable={!isEmpty(eventDetails) && checkEditable()}
        details={eventDetails}
        onEdit={onEdit}
        cancel={onCancel}
        loading={loading}
        downloadable={downloadable}
        onDownloadClick={onDownloadClick}
        FollowupButtonComponent={followupComponent}
        operationComponent={!isEmpty(eventDetails) ? operation() : undefined}
        editHoverText={
          Object.keys(eventDetails).length > 0
            ? translatedKeyValueText('edit_value_title', {
                value: eventDetails.noSectionFields.title.value,
              })
            : staticText.operational_view.event.edit_default
        }
      />
      {followupEventID && (
        <FollowupEventDetailsPanel
          followupEventID={followupEventID}
          editable={true}
          followupComponent={followupComponent}
        />
      )}
    </>
  );
};

EventDetails.propTypes = {
  toggleEditMode: PropTypes.func,
  toggleModal: PropTypes.func,
  editable: PropTypes.bool,
  id: PropTypes.string.isRequired,
  cancel: PropTypes.func.isRequired,
  fetchEventById: PropTypes.func,
  event: PropTypes.object,
  setMessage: PropTypes.func,
  resetMsg: PropTypes.func,
  openParentEvent: PropTypes.func,
  parentEventID: PropTypes.string,
  followupEventId: PropTypes.string,
  isSupervisor: PropTypes.array,
  resetEventDetail: PropTypes.func,
  removeSelectedEventId: PropTypes.func,
  loading: PropTypes.bool,
  downloadable: PropTypes.bool,
  OperationalComponent: PropTypes.object,
};

export default EventDetails;
